import SockJS from "sockjs-client";
import { Stomp } from "stompjs/lib/stomp.min.js";
import { localStorageKeys } from "../const";
import { checkAuth } from "../auth/authHelper";

class WebSocketConnection {
  constructor(url, headers = {}, authHeader = true) {
    this.url = url;
    this.headers = headers;
    this.stompClient = null;
    this.subscriptions = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000;
    this.authHeader = authHeader;
  }

  getSubscriptions() {
    return this.subscriptions;
  }

  async connect() {
    // transportservice rejects the STOMP CONNECT frame when the access token
    // has expired, so refresh it before reading it out of local storage.
    // Without this the socket connects with whatever token the previous
    // session left behind - stale on any page load made more than an hour
    // after login - and the server drops the connection right after it opens.
    if (this.authHeader) {
      await checkAuth();
    }

    return new Promise((resolve, reject) => {
      const ws = new SockJS(this.url);
      this.stompClient = Stomp.over(ws);
      // stompjs calls the error callback once for the ERROR frame and again
      // when the socket closes; only the first one may drive a retry.
      let settled = false;

      if (this.authHeader) {
        this.headers = {
          ...this.headers,
          [localStorageKeys.USER.USERNAME]: localStorage.getItem(
            localStorageKeys.USER.USERNAME,
          ),
          [localStorageKeys.ACCESS_TOKEN]: localStorage.getItem(
            localStorageKeys.ACCESS_TOKEN,
          ),
        };
      }

      this.stompClient.connect(
        this.headers,
        () => {
          settled = true;
          this.reconnectAttempts = 0;
          console.log("success......");
          resolve();
        },
        (error) => {
          this.onError(error);
          if (settled) return;
          settled = true;
          this.attemptReconnect(resolve, reject);
        },
      );
    });
  }

  attemptReconnect(resolve, reject) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max Reconnect attempts reached....");
      reject(new Error(`Could not connect to ${this.url}`));
      return;
    }

    setTimeout(() => {
      this.reconnectAttempts++;
      console.log("Reconnecting...");
      this.connect().then(resolve, reject);
    }, this.reconnectDelay);
  }

  subscribe(topic, onMessage) {
    if (this.stompClient && this.stompClient.connected) {
      const subscription = this.stompClient.subscribe(topic, (message) => {
        if (message.body) {
          try {
            const data = JSON.parse(message.body);
            onMessage(data);
          } catch (e) {
            console.error("Error parsing message - ", e);
          }
        }
      });
      this.subscriptions.push({ topic, subscription });
      return subscription;
    } else {
      console.log("Client not connected.... Attempting reconnect");
      return new Promise((resolve, reject) =>
        this.attemptReconnect(resolve, reject),
      );
    }
  }

  send(topic, header, body) {
    if (this.stompClient && this.stompClient.connected) {
      this.stompClient.send(topic, header, body);
    } else {
      console.error("Cannot send message.... Client not connected");
    }
  }

  unsubscribe(topic) {
    const subscriptionIndex = this.subscriptions.findIndex(
      (sub) => sub.topic === topic,
    );
    console.log(topic, subscriptionIndex);

    if (subscriptionIndex !== -1) {
      this.subscriptions[subscriptionIndex].subscription.unsubscribe();
      this.subscriptions.splice(subscriptionIndex, 1);
    } else {
      console.error("Empty subscription list");
    }
  }

  disconnect() {
    if (this.stompClient && this.stompClient.connected) {
      console.log("disconnecting");
      this.subscriptions.forEach((sub) => sub.subscription.unsubscribe());
      this.stompClient.disconnect(() => {
        console.log("Disconnected from", this.url);
      });
      this.subscriptions = [];
    } else {
      console.error("Client is not connected.");
    }
  }

  onError = (error) => {
    console.error("Connection error:", error);
  };
}

export default WebSocketConnection;
